/**
 * screenStatusWatcher — raises "screen went offline / back online"
 * notifications from device lists the dashboard already receives, WITHOUT any
 * dedicated polling.
 *
 * Fed from two sources sharing one module-level map:
 *   1. Dashboard's socket handler (tv:list:update — server-pushed, also fires
 *      on every connect/disconnect).
 *   2. ContentDashboard's existing 15s device fetch — reused, no extra request.
 *
 * DEBOUNCE / HYSTERESIS — why this isn't a naive status diff:
 * The backend marks a device "offline" the instant its socket disconnects and
 * "online"/"pending" again on reconnect. A flaky socket (common on these TV
 * boxes) flaps every couple of seconds even though the screen never actually
 * goes dark — a naive diff would fire a notification on every flap.
 *
 * So we collapse status to a binary REACHABLE (online|pending) vs OFFLINE, and
 * only notify once a device has *held* the new state for GRACE_MS. A blip that
 * reverts before the grace window elapses is cancelled silently. Because the
 * broadcast keeps arriving (~10s) and ContentDashboard polls (15s), a genuine
 * sustained change is always confirmed by a follow-up observation.
 */

import notificationStore from './notificationStore';

// A device must hold a new reachability state this long before we notify, so a
// flapping socket doesn't spam. Genuine offline/online survives this window.
export const GRACE_MS = 45_000;

// deviceId/tvId -> {
//   confirmed: 'reachable'|'offline',   // last state we treat as true (+ notified)
//   candidate: 'reachable'|'offline'|null,
//   since: number|null,                 // when `candidate` was first observed (ms)
//   projectId, label,                   // freshest metadata for the eventual notification
// }
const state = new Map();

// deviceId/tvId -> friendly name (the label the user assigned). The socket
// broadcast carries only deviceId for unassigned/registry devices, but the
// REST feed (ContentDashboard, Dashboard's tvs) carries displayName/tvId. We
// remember names across ALL feeds so a socket-triggered alert can still show
// the assigned name instead of the raw "Screen_<id>".
const names = new Map();

// Friendly label from a device record, in the same priority the dashboard
// uses: assigned displayName, then tvId, then location. Excludes the raw
// deviceId (that's the fallback, not a "name").
function friendlyName(d, key) {
  const n = d?.displayName || d?.tvId || d?.name || d?.location;
  return n && n !== key ? n : null;
}

/**
 * Record friendly names from a device list without raising any notification.
 * Call from any feed that carries names (e.g. the REST device list) so labels
 * resolve even when the alert is triggered by the name-less socket feed.
 */
export function registerScreenNames(deviceList) {
  if (!Array.isArray(deviceList)) return;
  for (const d of deviceList) {
    const key = d?.deviceId || d?.tvId;
    if (!key) continue;
    const n = friendlyName(d, key);
    if (n) names.set(key, n);
  }
}

const labelFor = (key, d) => names.get(key) || friendlyName(d, key) || key || 'A screen';

// Collapse raw registry status into reachability. online|pending = the device
// has a live socket (reachable); offline = no connection. Unknown → null (skip).
function reachability(status) {
  if (status === 'offline') return 'offline';
  if (status === 'online' || status === 'pending') return 'reachable';
  return null;
}

/**
 * Diff a device list against tracked state and notify on *sustained*
 * reachability changes. Safe to call from multiple sources. `now` is injectable
 * for tests.
 * @param {Array<{deviceId?:string,tvId?:string,status?:string,projectId?:string,project_id?:string,displayName?:string}>} deviceList
 * @param {number} [now]
 */
export function watchScreenStatuses(deviceList, now = Date.now()) {
  if (!Array.isArray(deviceList)) return;
  for (const d of deviceList) {
    const key = d?.deviceId || d?.tvId;
    if (!key) continue;
    const bin = reachability(d.status);
    if (bin === null) continue;

    const projectId = d.projectId || d.project_id || null;
    // Remember this device's friendly name if the feed carries one.
    const fn = friendlyName(d, key);
    if (fn) names.set(key, fn);
    const label = labelFor(key, d);
    let entry = state.get(key);

    // First sighting → establish a silent baseline (no notification on load).
    if (!entry) {
      state.set(key, { confirmed: bin, candidate: null, since: null, projectId });
      continue;
    }
    entry.projectId = projectId;

    // Matches the confirmed state → stable; cancel any pending flap.
    if (bin === entry.confirmed) {
      entry.candidate = null;
      entry.since = null;
      continue;
    }

    // Differs from confirmed → a candidate change. Start (or restart) timing.
    if (entry.candidate !== bin) {
      entry.candidate = bin;
      entry.since = now;
      continue;
    }

    // Candidate has persisted — confirm only once it has held past the grace
    // window, so transient flaps (which revert before GRACE_MS) never notify.
    if (now - entry.since >= GRACE_MS) {
      entry.confirmed = bin;
      entry.candidate = null;
      entry.since = null;
      if (bin === 'offline') {
        notificationStore.push({
          type: 'screen-offline',
          severity: 'warning',
          title: 'Screen went offline',
          message: `${label} has been offline for a while and stopped responding.`,
          deviceId: key,
          projectId,
        });
      } else {
        notificationStore.push({
          type: 'screen-online',
          severity: 'success',
          title: 'Screen back online',
          message: `${label} reconnected and is online again.`,
          deviceId: key,
          projectId,
        });
      }
    }
  }
}

export default watchScreenStatuses;
